import { useState, useCallback, useMemo } from 'react';
import { message } from "antd";
import useQueryApiClient from "../../../../utils/useQueryApiClient";
import { useCartState, useCartDispatch, CartActionType } from 'public/context/CartContext';
import { useTranslations } from "../../../../admin/pages/Coupons/hooks/use-translations";
import useCouponSummary, { CouponSummary } from './useCouponSummary';

export interface CouponValidationResponse {
  code: string;
  discounts: Record<number, number>;
  valid: boolean;
}

export function useCoupons() {
  const t = useTranslations()
  const dispatch = useCartDispatch();
  const { coupons, farmProducts } = useCartState();
  const summary: CouponSummary = useCouponSummary(farmProducts, coupons);
  const [code, setCode] = useState<string>('');

  const { appendData: validate, isLoading: validating } = useQueryApiClient({
    request: {
      url: `api/v2/coupons/validate`,
      method: 'POST',
    },
    onSuccess: (resp: CouponValidationResponse) => {
      if (resp.valid) {
        dispatch({
          type: CartActionType.ADD_COUPON,
          coupon: {
            code,
            productDiscounts: resp.discounts,
          },
        });
        setCode('');
      } else {
        message.error(t('cart_summary.coupon.error.default'));
      }
    },
    onError: () => {
      message.error(t('cart_summary.coupon.error.network'));
    },
  });

  const applyCoupon = useCallback(() => {
    if (coupons.find(c => c.code === code)) {
      message.error(t('cart_summary.coupon.error.code'));
      return;
    }

    const productIds = farmProducts
      .flatMap(farm => farm.products)
      .map(p => p.cartProduct?.id)
      .filter((id): id is number => Boolean(id));

    validate({ code, product_ids: productIds });
  }, [code, coupons, farmProducts, t, validate]);

  const removeCoupon = useCallback((codeToRemove: string) => {
    dispatch({ type: CartActionType.REMOVE_COUPON, code: codeToRemove });
  }, [dispatch]);

  return useMemo(() => ({
    code,
    setCode,
    coupons,
    validating,
    applyCoupon,
    removeCoupon,
    ...summary,
  }), [code, coupons, validating, applyCoupon, removeCoupon, summary]);
}